import { ArrowLeftIcon } from '@heroicons/react/16/solid';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { Button } from '../catalyst-ui-kit/button';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';

export type PanelDirection = 'left' | 'right';

const AnimateHeight = ({
  children,
  isActive = true,
}: {
  children: React.ReactNode;
  isActive: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | 'auto'>('auto');

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.offsetHeight);
    }
  }, [children, isActive]);

  return isActive ? (
    <motion.div
      animate={{ height: isActive ? height : undefined }}
      initial={false}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="overflow-hidden"
    >
      <div ref={ref}>{children}</div>
    </motion.div>
  ) : (
    <div ref={ref}>{children}</div>
  );
};

type PanelModalProps = {
  open: boolean;
  panelId: string;
  title: React.ReactNode;
  currentPanel: React.FC;
  direction?: PanelDirection;
  panelClassName?: string;
  onClose: () => void;
  onNavigateBack?: () => void;
};

export const RenderPanelContent: React.FC<{ component: React.ReactNode | React.FC }> = React.memo(
  ({ component: Component }) => {
    if (Component === null || Component === undefined) {
      return null;
    }

    if (React.isValidElement(Component)) {
      return Component;
    }

    if (typeof Component === 'function') {
      return <Component />;
    }

    return Component;
  }
);

/** Modal that can transition between panels. */
export const PanelModal: React.FC<PanelModalProps> = ({
  open,
  title,
  panelId,
  currentPanel,
  direction = 'right',
  panelClassName,
  onClose,
  onNavigateBack,
}) => {
  // Track previous panel content so that we can slide it out.
  const [prevPanelState, setPrevPanelState] = useState<{
    content: React.ReactNode | React.FC;
    key: number;
  } | null>(null);

  const [currentPanelState, setCurrentPanelState] = useState<{
    content: React.ReactNode | React.FC;
    key: number;
  } | null>(null);

  const nextKeyRef = useRef(1);
  const isFirstRender = useRef(true);
  const lastPanelRef = useRef<React.FC | null>(currentPanel);
  const currentPanelRef = useRef<HTMLDivElement>(null);
  const [animating, setAnimating] = useState(false);

  // Reset state when closed.
  useEffect(() => {
    let closeTimeout: NodeJS.Timeout;
    if (!open) {
      // Wait for modal's close animation before resetting state.
      closeTimeout = setTimeout(() => {
        setCurrentPanelState(null);
        setPrevPanelState(null);
        isFirstRender.current = true;
        lastPanelRef.current = null;
      }, 300);
    }
    return () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
      }
    };
  }, [open]);

  // Update panel state.
  useEffect(() => {
    if (isFirstRender.current) {
      const key = nextKeyRef.current++;
      setCurrentPanelState({ content: currentPanel, key });
      isFirstRender.current = false;
      lastPanelRef.current = currentPanel;
      return;
    } else if (lastPanelRef.current !== currentPanel) {
      if (currentPanelState) {
        setPrevPanelState(currentPanelState);
      }

      const newKey = nextKeyRef.current++;
      setCurrentPanelState({ content: currentPanel, key: newKey });
      setAnimating(true);

      lastPanelRef.current = currentPanel;
    }

    // Using onAnimationComplete was causing scrollbar to appear for a
    // split-second, so we use a timeout instead.
    const timeout = setTimeout(() => {
      setPrevPanelState(null);
      setAnimating(false);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [currentPanel, currentPanelState]);

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      title={
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={panelId}
            initial={{ opacity: 0, x: direction === 'right' ? 10 : -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction === 'right' ? -10 : 10 }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
            className="flex gap-2"
          >
            {onNavigateBack && (
              <Button text color="secondary" onClick={onNavigateBack}>
                <ArrowLeftIcon className="!size-6 max-lg:ml-2" />
              </Button>
            )}
            <span className="font-medium text-contentPrimary">{title}</span>
          </motion.div>
        </AnimatePresence>
      }
      childrenContainerClassName="p-0"
      panelClassName={twMerge('relative transition-all duration-500', panelClassName)}
    >
      <div
        className={twJoin('relative overflow-hidden')}
        style={{
          transition: 'height 0.3s ease-in-out',
        }}
      >
        <AnimatePresence initial={false}>
          {/* Previous panel, only shown while sliding out */}
          {prevPanelState && (
            <motion.div
              key={`prev-${prevPanelState.key}`}
              initial={{ x: 0, opacity: 1 }}
              animate={{
                x: direction === 'right' ? -300 : 300,
                opacity: 0,
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="absolute inset-x-0 top-0 w-full"
            >
              <RenderPanelContent component={prevPanelState.content} />
            </motion.div>
          )}

          {currentPanelState && (
            <AnimateHeight isActive={animating}>
              <motion.div
                key={`current-${currentPanelState.key}`}
                ref={currentPanelRef}
                initial={
                  prevPanelState === null
                    ? { x: 0, opacity: 1 }
                    : {
                        x: direction === 'right' ? 300 : -300,
                        opacity: 0,
                      }
                }
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className={twJoin('w-full')}
              >
                <RenderPanelContent component={currentPanelState.content} />
              </motion.div>
            </AnimateHeight>
          )}
        </AnimatePresence>
      </div>
    </ResponsiveModal>
  );
};
